import { Checkbox, MenuItem, Select } from "@mui/material";
import MDBox from "components/MDBox";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { WorksheetHeaderUnit, WorksheetPricingMode } from "generated/graphql";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { toDollars } from "utils/money/toDollars";
import {
  HEADER_UNIT_OPTIONS,
  capturePricingBaseline,
  dollarsToCents,
  parseDecimalInput,
} from "./worksheetFormValues";
import { compactSelectSx, worksheetHeaderGridTemplate } from "./worksheetStyles";
import { WorksheetTotals } from "./useWorksheetTotals";

type WorksheetHeaderFieldsProps = {
  isReadOnly?: boolean;
  totals: WorksheetTotals;
};

const overriddenSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: draftColors.red,
  },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: draftColors.red,
  },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: draftColors.red,
  },
} as const;

export default function WorksheetHeaderFields({ isReadOnly, totals }: WorksheetHeaderFieldsProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const values = useWatch({ control }) as WorksheetItemInput;
  const { total, enteredCosts, costCents, profitCents, marginPercentage } = totals;

  const startOverride = (target: "profit" | "margin" | "total" | "rate") => {
    if (values.pricingOverrideField !== target) {
      setValue("pricingBaseline", capturePricingBaseline(values), { shouldDirty: true });
    }
  };

  const clearActiveTarget = (target: "profit" | "margin") => {
    if (values.pricingOverrideField !== target) {
      return;
    }

    setValue("fixedTotal", toDollars(total), { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
    setValue("pricingOverrideField", "none", { shouldDirty: true });
    setValue("pricingBaseline", null, { shouldDirty: true });
  };

  const handleSellingRateChange = (value: string) => {
    startOverride("rate");
    setValue("sellingRate", value, { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.RATE, { shouldDirty: true });
    setValue("pricingOverrideField", "rate", { shouldDirty: true });
  };

  const handleTotalChange = (value: string) => {
    startOverride("total");
    setValue("fixedTotal", value, { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
    setValue("pricingOverrideField", "total", { shouldDirty: true });
  };

  const handleProfitChange = (value: string) => {
    if (!value.trim()) {
      clearActiveTarget("profit");
      return;
    }

    startOverride("profit");
    const profit = dollarsToCents(value);
    setValue("fixedTotal", toDollars(costCents + profit), { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
    setValue("pricingOverrideField", "profit", { shouldDirty: true });
  };

  const handleMarginChange = (value: string) => {
    if (!value.trim()) {
      clearActiveTarget("margin");
      return;
    }

    const margin = parseDecimalInput(value);

    if (enteredCosts === null || margin >= 100 || (costCents === 0 && margin > 0)) {
      return;
    }

    startOverride("margin");
    const totalFromMargin = Math.round(costCents / (1 - margin / 100));
    setValue("fixedTotal", toDollars(totalFromMargin), { shouldDirty: true });
    setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
    setValue("pricingOverrideField", "margin", { shouldDirty: true });
  };

  return (
    <MDBox
      display="grid"
      gridTemplateColumns={worksheetHeaderGridTemplate}
      gap="6px"
      alignItems="end"
    >
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Item Name
        </UiTypography>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <UiInput
              value={field.value}
              readOnly={isReadOnly}
              placeholder="Item name"
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Quantity
        </UiTypography>
        <Controller
          control={control}
          name="sellingQuantity"
          render={({ field }) => (
            <UiInput
              value={field.value}
              readOnly={isReadOnly}
              placeholder="0"
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Unit
        </UiTypography>
        <Controller
          control={control}
          name="unit"
          render={({ field }) => (
            <Select
              value={field.value}
              disabled={isReadOnly}
              sx={{ ...compactSelectSx, width: "100%" }}
              onChange={(event) => field.onChange(event.target.value as WorksheetHeaderUnit)}
            >
              {HEADER_UNIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: "11px" }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Unit Price
        </UiTypography>
        <Controller
          control={control}
          name="sellingRate"
          render={({ field }) => (
            <UiInput
              value={field.value}
              readOnly={isReadOnly}
              textAlign="right"
              placeholder="0.00"
              sx={values.pricingOverrideField === "rate" ? overriddenSx : undefined}
              onChange={(event) => handleSellingRateChange(event.target.value)}
            />
          )}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Profit
        </UiTypography>
        <UiInput
          value={toDollars(profitCents)}
          readOnly={isReadOnly}
          textAlign="right"
          sx={values.pricingOverrideField === "profit" ? overriddenSx : undefined}
          onChange={(event) => handleProfitChange(event.target.value)}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Margin %
        </UiTypography>
        <UiInput
          value={marginPercentage === null ? "" : marginPercentage.toFixed(2)}
          readOnly={isReadOnly || enteredCosts === null}
          textAlign="right"
          placeholder="0"
          sx={values.pricingOverrideField === "margin" ? overriddenSx : undefined}
          onChange={(event) => handleMarginChange(event.target.value)}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Discount
        </UiTypography>
        <Controller
          control={control}
          name="discount"
          render={({ field }) => (
            <UiInput
              value={field.value}
              readOnly={isReadOnly}
              textAlign="right"
              placeholder="0.00"
              onChange={(event) => field.onChange(event.target.value)}
            />
          )}
        />
      </MDBox>
      <MDBox>
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Total
        </UiTypography>
        <UiInput
          value={
            values.pricingMode === WorksheetPricingMode.FIXED ? values.fixedTotal : toDollars(total)
          }
          readOnly={isReadOnly}
          textAlign="right"
          sx={values.pricingOverrideField === "total" ? overriddenSx : undefined}
          onChange={(event) => handleTotalChange(event.target.value)}
        />
      </MDBox>
      <MDBox display="flex" flexDirection="column" alignItems="center">
        <UiTypography uiVariant="label-xs" sx={{ color: draftColors.textLabel }}>
          Optional
        </UiTypography>
        <Controller
          control={control}
          name="isOptional"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              disabled={isReadOnly}
              size="small"
              sx={{ p: 0 }}
              onChange={(event) => field.onChange(event.target.checked)}
            />
          )}
        />
      </MDBox>
    </MDBox>
  );
}
